import { getEventOrganizationName } from './eventOrganization';
import {
  getEventSearchKeywords,
  getEventSearchTitle,
} from './eventSearchThemes';
import { isPlaceholderLocation, normalizeCityName } from './eventLocationDisplay';
import { cleanDescription } from './eventDescription';

/**
 * Build and inject schema.org Event JSON-LD for event detail pages.
 * Must tolerate messy scraper data — never throw to callers of buildEventJsonLd.
 */

const SITE_ORIGIN = 'https://www.myeventsbridge.com';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/logo-new.png`;
const JSON_LD_SCRIPT_ID = 'event-json-ld';
const META_MARK_ATTR = 'data-event-json-ld';

function normalizePrice(rawPrice) {
  if (typeof rawPrice !== 'string') return '';
  let s = rawPrice.trim();
  if (!s) return '';
  if (/not found/i.test(s)) return '';
  s = s.replace(/^price\b\s*/i, '');
  s = s.replace(/^[:\-–]\s*/, '');
  return s.trim();
}

function parseClockToHhMm(timePart) {
  if (!timePart) return null;
  const s = String(timePart).trim().toLowerCase();
  const m = s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = (m[3] || '').toLowerCase();
  if (ampm === 'pm' && hours < 12) hours += 12;
  if (ampm === 'am' && hours === 12) hours = 0;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function parseTimeRange(timeText) {
  if (!timeText) return { start: null, end: null };
  const s = String(timeText).trim();
  const parts = s.split(/\s*(?:-|\u2013|to)\s+/i).map((p) => p.trim()).filter(Boolean);
  const start = parseClockToHhMm(parts[0] || s);
  const end = parts.length >= 2 ? parseClockToHhMm(parts[1]) : null;
  return { start, end };
}

/** Only accept absolute calendar dates (YYYY-MM-DD). Skip relative dates. */
function isAbsoluteDate(dateStr) {
  return typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim());
}

/**
 * Pacific offset for a given YYYY-MM-DD (e.g. "-07:00" or "-08:00").
 */
function getPacificOffset(dateStr, hhmm = '12:00') {
  try {
    const wallClock = new Date(`${dateStr}T${hhmm}:00Z`);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'shortOffset',
    });
    // Resolve the offset at the event's actual time, including nights when DST changes.
    const initial = formatter.formatToParts(wallClock).find(p => p.type === 'timeZoneName')?.value || '';
    const initialMatch = initial.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/i);
    if (!initialMatch) return '-07:00';
    const offsetMinutes = (Number(initialMatch[2]) * 60 + Number(initialMatch[3] || 0)) *
      (initialMatch[1] === '+' ? 1 : -1);
    const probe = new Date(wallClock.getTime() - offsetMinutes * 60000);
    const parts = formatter.formatToParts(probe);
    const tz = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const m = tz.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/i);
    if (!m) return '-07:00';
    const sign = m[1];
    const hours = String(parseInt(m[2], 10)).padStart(2, '0');
    const mins = m[3] ? m[3] : '00';
    return `${sign}${hours}:${mins}`;
  } catch {
    return '-07:00';
  }
}

function toPacificIso(dateStr, hhmm) {
  if (!isAbsoluteDate(dateStr) || !hhmm) return null;
  const offset = getPacificOffset(dateStr.trim(), hhmm);
  return `${dateStr.trim()}T${hhmm}:00${offset}`;
}

function parseLocation(location) {
  if (isPlaceholderLocation(location)) {
    return { venue: null, address: null };
  }
  const parts = String(location).split(/[•,]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { venue: parts[0], address: parts.slice(1).join(', ') };
  }
  if (/\d/.test(location) || /st\b|ave\b|blvd\b|road\b|dr\b/i.test(location)) {
    return { venue: null, address: location };
  }
  return { venue: location, address: null };
}

function buildPostalAddressFromStructured(addr) {
  if (!addr || typeof addr !== 'object') return null;
  const postal = { '@type': 'PostalAddress' };
  if (addr.addressLine1) postal.streetAddress = addr.addressLine1;
  if (addr.addressLine2) {
    postal.streetAddress = postal.streetAddress
      ? `${postal.streetAddress}, ${addr.addressLine2}`
      : addr.addressLine2;
  }
  if (addr.city) postal.addressLocality = normalizeCityName(addr.city);
  if (addr.state) postal.addressRegion = addr.state;
  if (addr.postalCode) postal.postalCode = addr.postalCode;
  if (addr.country) postal.addressCountry = addr.country;
  if (!postal.streetAddress && !postal.addressLocality && !addr.formattedAddress) {
    return null;
  }
  if (!postal.streetAddress && addr.formattedAddress && !postal.addressLocality) {
    postal.streetAddress = addr.formattedAddress;
  }
  return postal;
}

function buildLocation(event) {
  const structured = event?.Address;
  if (structured) {
    const place = { '@type': 'Place' };
    if (structured.venue) place.name = structured.venue;
    const postal = buildPostalAddressFromStructured(structured);
    if (postal) place.address = postal;
    else if (structured.formattedAddress) {
      place.address = {
        '@type': 'PostalAddress',
        streetAddress: structured.formattedAddress,
      };
    }
    if (place.name || place.address) return place;
  }

  const parsed = parseLocation(event?.Location);
  if (!parsed.venue && !parsed.address) return null;

  const place = { '@type': 'Place' };
  if (parsed.venue) place.name = parsed.venue;
  if (parsed.address) {
    place.address = {
      '@type': 'PostalAddress',
      streetAddress: parsed.address,
    };
  } else if (parsed.venue) {
    place.address = {
      '@type': 'PostalAddress',
      streetAddress: parsed.venue,
    };
  }
  return place;
}

function buildOffers(priceText, offerUrl) {
  const normalized = normalizePrice(priceText);
  if (!normalized) return null;

  if (/^free$/i.test(normalized)) {
    return {
      '@type': 'Offer',
      url: offerUrl,
      availability: 'https://schema.org/InStock',
      price: '0',
      priceCurrency: 'USD',
    };
  }

  const amounts = Array.from(normalized.matchAll(/\$?\s*(\d+(?:\.\d{1,2})?)/g))
    .map((match) => match[1]);
  if (!amounts.length) return null;

  if (amounts.length > 1) {
    return {
      '@type': 'AggregateOffer',
      url: offerUrl,
      availability: 'https://schema.org/InStock',
      lowPrice: amounts[0],
      highPrice: amounts[amounts.length - 1],
      priceCurrency: 'USD',
    };
  }

  return {
    '@type': 'Offer',
    url: offerUrl,
    availability: 'https://schema.org/InStock',
    price: amounts[0],
    priceCurrency: 'USD',
  };
}

function absoluteHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim())
    ? value.trim()
    : null;
}

function eventCity(event) {
  return normalizeCityName(event?.Address?.city || '');
}

function displayShortDate(dateText) {
  if (!isAbsoluteDate(dateText)) return dateText || '';
  const [year, month, day] = dateText.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function truncateAtWord(value, maxLength) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  const candidate = text.slice(0, Math.max(0, maxLength - 1));
  const boundary = candidate.lastIndexOf(' ');
  const cut = boundary > Math.floor(maxLength * 0.6)
    ? candidate.slice(0, boundary)
    : candidate;
  return `${cut.replace(/[\s,;:\-\u2013.]+$/g, '')}…`;
}

function buildSeoTitle(event) {
  const name = getEventSearchTitle(event);
  const city = eventCity(event);
  return `${name}${city ? ` in ${city}` : ''} | My Events Bridge`;
}

function buildSeoDescription(event) {
  const name = getEventSearchTitle(event);
  const city = eventCity(event);
  const date = displayShortDate(event?.Date);
  const category = Array.isArray(event?.categories) ? event.categories[0] : '';
  const type = category ? category.toLowerCase() : 'dance event';
  const context = [city, date].filter(Boolean).join(', ');
  const suffix = ' My Events Bridge is a San Francisco Bay Area dance events guide.';
  const lead = `${name} — ${type}${context ? ` in ${context}` : ''}.`;
  const leadBudget = 165 - suffix.length;
  return `${truncateAtWord(lead, leadBudget)}${suffix}`;
}

/**
 * @param {object} event - raw event from API
 * @param {string} shareId
 * @param {{ imageUrl?: string }} [options]
 * @returns {object | null} JSON-LD object or null if event has no usable name
 */
export function buildEventJsonLd(event, shareId, options = {}) {
  try {
    if (!event || typeof event !== 'object') return null;
    const sourceName = (event['Event Name'] || '').toString().trim();
    if (!sourceName || sourceName.includes('No event')) return null;
    const name = getEventSearchTitle(event);

    const eventUrl = `${SITE_ORIGIN}/app/event/${shareId}/`;
    const { start, end } = parseTimeRange(event.Time);
    const startDate = toPacificIso(event.Date, start);
    const startDateFallback =
      !startDate && isAbsoluteDate(event.Date) ? event.Date.trim() : null;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name,
      url: eventUrl,
      mainEntityOfPage: eventUrl,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      image: options.imageUrl || DEFAULT_IMAGE,
      publisher: {
        '@type': 'Organization',
        name: 'My Events Bridge',
        url: SITE_ORIGIN,
      },
    };

    const organizerName = getEventOrganizationName(event);
    const officialEventUrl = absoluteHttpUrl(event.EventURL);
    if (organizerName) {
      schema.organizer = {
        '@type': 'Organization',
        name: organizerName,
        ...(officialEventUrl ? { url: officialEventUrl } : {}),
      };
      schema.performer = {
        '@type': 'PerformingGroup',
        name: organizerName,
      };
    }

    const keywords = getEventSearchKeywords(event);
    if (keywords.length) schema.keywords = keywords.join(', ');

    const description = cleanDescription(event.Description || '');
    if (description) schema.description = description;

    if (startDate) schema.startDate = startDate;
    else if (startDateFallback) schema.startDate = startDateFallback;

    let endDay = event.Date;
    if (event.source === 'manual' && start && end && end < start && isAbsoluteDate(endDay)) {
      const next = new Date(`${endDay.trim()}T12:00:00Z`);
      next.setUTCDate(next.getUTCDate() + 1);
      endDay = next.toISOString().slice(0, 10);
    }
    const endDate = toPacificIso(endDay, end);
    if (endDate) schema.endDate = endDate;

    const location = buildLocation(event);
    if (!location) return null;
    schema.location = location;

    const ticketUrl = absoluteHttpUrl(event.TicketURL);
    const offers = buildOffers(event.price, ticketUrl || eventUrl);
    if (offers) schema.offers = offers;

    const sameAs = officialEventUrl || absoluteHttpUrl(event.Details);
    if (sameAs) schema.sameAs = sameAs;

    return schema;
  } catch (err) {
    console.warn('buildEventJsonLd failed:', err);
    return null;
  }
}

/**
 * Set meta content; track prior value (or "created") for cleanup.
 * @param {Array<{el: Element, previous: string|null, created: boolean}>} snapshots
 */
function setMetaContent(snapshots, selector, attrName, attrValue, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    el.setAttribute(META_MARK_ATTR, '1');
    document.head.appendChild(el);
    snapshots.push({ el, previous: null, created: true });
  } else {
    snapshots.push({ el, previous: el.getAttribute('content'), created: false });
  }
  el.setAttribute('content', content);
}

/**
 * Inject JSON-LD and update document title / basic meta for the event.
 * Returns a cleanup function that restores prior title and removes injected nodes.
 */
export function applyEventSeo(event, shareId) {
  const previousTitle = document.title;
  const metaSnapshots = [];
  const schema = buildEventJsonLd(event, shareId, {
    imageUrl:
      typeof event?.ImageURL === 'string' && /^https?:\/\//i.test(event.ImageURL.trim())
        ? event.ImageURL.trim()
        : undefined,
  });

  let script = document.getElementById(JSON_LD_SCRIPT_ID);
  if (schema) {
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSON_LD_SCRIPT_ID;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  } else if (script) {
    script.remove();
  }

  const name = getEventSearchTitle(event);
  if (name) {
    document.title = buildSeoTitle(event);
  }

  const description =
    buildSeoDescription(event) ||
    (name ? `${name} — event details on My Events Bridge` : '');
  const eventUrl = `${SITE_ORIGIN}/app/event/${shareId}/`;
  const image = (schema && schema.image) || DEFAULT_IMAGE;

  if (description) {
    const d = description;
    setMetaContent(metaSnapshots, 'meta[name="description"]', 'name', 'description', d);
    setMetaContent(metaSnapshots, 'meta[property="og:description"]', 'property', 'og:description', d);
    setMetaContent(metaSnapshots, 'meta[name="twitter:description"]', 'name', 'twitter:description', d);
  }
  if (name) {
    const socialTitle = buildSeoTitle(event);
    setMetaContent(metaSnapshots, 'meta[property="og:title"]', 'property', 'og:title', socialTitle);
    setMetaContent(metaSnapshots, 'meta[name="twitter:title"]', 'name', 'twitter:title', socialTitle);
  }
  setMetaContent(metaSnapshots, 'meta[property="og:url"]', 'property', 'og:url', eventUrl);
  setMetaContent(metaSnapshots, 'meta[property="og:image"]', 'property', 'og:image', image);
  setMetaContent(metaSnapshots, 'meta[name="twitter:image"]', 'name', 'twitter:image', image);

  let canonical = document.head.querySelector('link[rel="canonical"]');
  const previousCanonical = canonical ? canonical.getAttribute('href') : null;
  const canonicalCreated = !canonical;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute(META_MARK_ATTR, '1');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', eventUrl);

  return function cleanupEventSeo() {
    const s = document.getElementById(JSON_LD_SCRIPT_ID);
    if (s) s.remove();
    document.title = previousTitle;

    metaSnapshots.forEach(({ el, previous, created }) => {
      if (created) el.remove();
      else if (previous != null) el.setAttribute('content', previous);
    });

    if (canonicalCreated && canonical) canonical.remove();
    else if (previousCanonical != null && canonical) {
      canonical.setAttribute('href', previousCanonical);
    }
  };
}

/**
 * Head tags for an event id that is not in the current dataset.
 * Direct requests 404 in Apache; this covers in-app navigation, where the
 * document was already served and still has the listing canonical.
 */
export function applyUnavailableEventSeo() {
  const previousTitle = document.title;
  document.title = 'Event not found | My Events Bridge';

  const metaSnapshots = [];
  setMetaContent(metaSnapshots, 'meta[name="robots"]', 'name', 'robots', 'noindex, follow');

  const canonical = document.head.querySelector('link[rel="canonical"]');
  const canonicalParent = canonical ? canonical.parentNode : null;
  const canonicalNext = canonical ? canonical.nextSibling : null;
  if (canonical) canonical.remove();

  const jsonLd = document.getElementById(JSON_LD_SCRIPT_ID);
  if (jsonLd) jsonLd.remove();

  return function cleanupUnavailableEventSeo() {
    document.title = previousTitle;
    if (canonical && canonicalParent) {
      canonicalParent.insertBefore(canonical, canonicalNext);
    }
    metaSnapshots.forEach(({ el, previous, created }) => {
      if (created) el.remove();
      else if (previous != null) el.setAttribute('content', previous);
    });
  };
}

export const __testables = {
  cleanDescription,
  normalizePrice,
  parseTimeRange,
  isAbsoluteDate,
  toPacificIso,
  parseLocation,
  buildLocation,
  buildOffers,
  buildSeoTitle,
  buildSeoDescription,
  truncateAtWord,
  SITE_ORIGIN,
  DEFAULT_IMAGE,
  JSON_LD_SCRIPT_ID,
};
